<template>
  <section class="sh-messaging-main sh-new-message-pane">
    <header class="sh-new-message-header">
      <span class="sh-new-message-title">New Message</span>
      <button class="sh-new-message-cancel" type="button" @click="$emit('cancel')">
        Cancel
      </button>
    </header>

    <div class="sh-new-message-form">
      <!-- Source rally -->
      <div class="sh-new-message-field" v-click-outside="closeRallyList">
        <div class="sh-new-message-select" @click="toggleRallyList">
          <span :class="['sh-new-message-select-value', { 'is-placeholder': !selectedRally }]">
            {{ selectedRally ? rallyTitle(selectedRally) : 'Choose Source Rally' }}
          </span>
          <i :class="['fal', rallyListOpen ? 'fa-chevron-up' : 'fa-chevron-down']" aria-hidden="true"></i>
        </div>
        <div
          v-if="rallyListOpen"
          ref="rallyList"
          class="sh-new-message-options"
          @scroll="onRallyListScroll"
        >
          <input
            ref="rallyFilterInput"
            v-model="rallyFilter"
            class="sh-rally-filter-input"
            type="text"
            placeholder="Filter rallies..."
          />
          <div
            v-for="rally in filteredRallies"
            :key="rally.id"
            class="sh-new-message-option"
            @click="selectRally(rally)"
          >
            {{ rallyTitle(rally) }}
          </div>
          <div v-if="loadingRallies" class="sh-new-message-option sh-rally-list-note">
            Loading&hellip;
          </div>
          <div
            v-else-if="filteredRallies.length === 0"
            class="sh-new-message-option sh-rally-list-note"
          >
            No rallies match
          </div>
          <!-- Explicit fallback for when scroll-to-load can't fire (touch
               scrolling the outer pane, or a list too short to scroll). -->
          <div
            v-else-if="!allRalliesLoaded && !rallyFilter.trim()"
            class="sh-new-message-option sh-rally-list-more"
            @click.stop="loadMoreRallies"
          >
            Load more rallies&hellip;
          </div>
        </div>
      </div>

      <!-- Recipients -->
      <div v-if="selectedRally" class="sh-new-message-field sh-new-message-recipients" v-click-outside="closeMemberList">
        <span
          v-for="member in selectedMembers"
          :key="member.userId"
          class="sh-recipient-chip"
        >
          {{ member.name }}
          <i class="fal fa-times" aria-hidden="true" @click="removeMember(member)"></i>
        </span>
        <!-- .stop is required: this span is removed from the DOM the moment
             memberSearchOpen flips, so if the click bubbles to the document,
             the v-click-outside handler no longer sees it as "inside" and
             instantly closes the search input again. -->
        <span class="sh-add-member" v-if="!memberSearchOpen && canAddRecipient" @click.stop="openMemberSearch">
          <i class="fal fa-plus" aria-hidden="true"></i> Add Member
        </span>
        <input
          v-else-if="memberSearchOpen && canAddRecipient"
          ref="memberSearchInput"
          v-model="memberQuery"
          class="sh-member-search-input"
          type="text"
          placeholder="Search members..."
          @input="onMemberQueryChange"
        />
        <div v-if="memberSearchOpen && canAddRecipient && memberResults.length > 0" class="sh-new-message-options sh-member-results">
          <div
            v-for="member in memberResults"
            :key="member.id"
            class="sh-new-message-option"
            @click="addMember(member)"
          >
            {{ member.attributes['first-name'] }} {{ member.attributes['last-name'] }}
          </div>
        </div>
      </div>
    </div>

    <footer class="sh-messaging-footer">
      <div class="sh-messaging-input-row">
        <input
          class="sh-messaging-input"
          type="text"
          v-model="messageText"
          placeholder="Create a message..."
          :disabled="!canSend"
          @keyup.enter="send"
        />
        <button
          type="button"
          class="sh-messaging-attach-btn"
          aria-label="Send message"
          :disabled="!canSend || !messageText"
          @click.prevent="send"
        >
          <i class="fal fa-paper-plane" aria-hidden="true"></i>
        </button>
      </div>
    </footer>
  </section>
</template>

<script>
import { groupMessagingEnabled } from "../common/features";
import { mapState, mapActions } from "vuex";
import { userInfo } from "../common/user-info";
import clickOutside from "../directives/v-click-outside.js";

export default {
  name: "NewMessageComposer",
  // The global click-outside registration is commented out in main.js, so
  // register the directive locally.
  directives: { "click-outside": clickOutside },
  data() {
    return {
      rallyListOpen: false,
      rallyFilter: "",
      loadingRallies: false,
      drainingRallies: false,
      fillingRallies: false,
      selectedRally: null,
      memberSearchOpen: false,
      memberQuery: "",
      selectedMembers: [],
      messageText: "",
      searchDebounce: null,
      sending: false
    };
  },
  computed: {
    ...mapState("rallies", ["myRallies"]),
    ...mapState("memberships", ["memberSearchResults"]),
    // Only offer rallies whose membership will pass the channel service's
    // rally gate (at_least_readable_friend?): an Invited-but-not-joined
    // membership 403s on the channels GET. Items without a membership
    // record (fallback shapes) are kept — we can't judge them client-side.
    availableRallies() {
      return (this.myRallies || []).filter(r => {
        const status = r?.membership?.attributes?.status;
        // Placeholder rally objects (id-only fallback shapes the store
        // seeds before the rally payload hydrates) have no title and
        // rendered as blank rows in the dropdown — hide them.
        return (!status || status === "Active") && this.rallyTitle(r) !== "";
      });
    },
    // Total from the memberships endpoint's meta; null until the first
    // page lands. myRallies grows page-by-page (6 per fetch) toward it.
    totalRallyCount() {
      const count = this.$store.state.rallies.resultCountByRallyId[1];
      return count === undefined ? null : count;
    },
    allRalliesLoaded() {
      return this.totalRallyCount !== null && (this.myRallies || []).length >= this.totalRallyCount;
    },
    filteredRallies() {
      const query = this.rallyFilter.trim().toLowerCase();
      if (!query) { return this.availableRallies; }
      return this.availableRallies.filter(r => this.rallyTitle(r).toLowerCase().includes(query));
    },
    memberResults() {
      const me = parseInt(userInfo().user_id);
      const chosen = new Set(this.selectedMembers.map(m => parseInt(m.userId)));
      const results = Array.isArray(this.memberSearchResults) ? this.memberSearchResults : [];
      return results.filter(m => {
        const uid = parseInt(m.attributes && m.attributes["user-id"]);
        // Active members only — searchMembers also returns Invited/
        // Requested/Affiliated, who haven't actually joined the rally yet.
        return uid && uid !== me && !chosen.has(uid) && m.attributes.status === "Active";
      });
    },
    canSend() {
      return !!this.selectedRally && this.selectedMembers.length > 0 && !this.sending;
    },
    groupMessagingOn() {
      return groupMessagingEnabled(this.selectedRally);
    },
    // Without group messaging a thread has exactly one recipient: once one
    // is chosen the picker simply goes away (no hint that more is possible).
    canAddRecipient() {
      return this.groupMessagingOn || this.selectedMembers.length === 0;
    }
  },
  watch: {
    rallyFilter(value) {
      if (value && value.trim()) { this.ensureAllRalliesLoaded(); }
    }
  },
  async created() {
    if (!this.myRallies || this.myRallies.length === 0) {
      await this.fetchMyRalliesWithInfo({});
    }
    this.fillRallyList();
  },
  methods: {
    ...mapActions("rallies", ["fetchMyRalliesWithInfo"]),
    ...mapActions("memberships", ["searchMembers"]),
    ...mapActions("channels", ["fetchOrCreateThread"]),
    ...mapActions("contents", ["createContents"]),
    rallyTitle(rally) {
      return rally?.rally?.attributes?.title || rally?.attributes?.title || "";
    },
    selectRally(rally) {
      this.selectedRally = rally;
      this.rallyListOpen = false;
      // Recipients are rally-scoped; changing rally resets the selection
      // AND the search results still sitting in the store — otherwise the
      // previous rally's people reappear when the search reopens.
      this.selectedMembers = [];
      this.memberQuery = "";
      this.memberSearchOpen = false;
      clearTimeout(this.searchDebounce);
      this.$store.commit("memberships/setMemberSearchResults", { members: [] });
    },
    closeRallyList() {
      this.rallyListOpen = false;
      this.rallyFilter = "";
    },
    toggleRallyList() {
      this.rallyListOpen = !this.rallyListOpen;
      if (this.rallyListOpen) {
        this.$nextTick(() => {
          this.$refs.rallyFilterInput?.focus();
          this.fillRallyList();
        });
      } else {
        this.rallyFilter = "";
      }
    },
    async loadMoreRallies() {
      if (this.loadingRallies || this.allRalliesLoaded) { return; }
      this.loadingRallies = true;
      try {
        // Parameterless call fetches the NEXT page (the store tracks the
        // page cursor in maxPageByRallyId) and appends to myRallies.
        await this.fetchMyRalliesWithInfo({});
      } finally {
        this.loadingRallies = false;
      }
    },
    // Pages are 6 rallies and the dropdown is capped at 240px, so the first
    // page(s) often don't overflow — and scroll-to-load can't fire on a list
    // that doesn't scroll. Keep pulling pages until the list is taller than
    // its box (or everything is loaded) so the scroll handler can take over.
    async fillRallyList() {
      if (this.fillingRallies) { return; }
      this.fillingRallies = true;
      try {
        let guard = 0;
        while (this.rallyListOpen && !this.allRalliesLoaded && guard < 10) {
          await this.$nextTick();
          const el = this.$refs.rallyList;
          if (!el || el.scrollHeight > el.clientHeight + 8) { break; }
          await this.loadMoreRallies();
          guard += 1;
        }
      } finally {
        this.fillingRallies = false;
      }
    },
    onRallyListScroll(event) {
      const el = event.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.loadMoreRallies();
      }
    },
    // Client-side filtering can only match pages already fetched, so once
    // the user starts typing, drain the remaining pages in the background
    // until the filter is searching the full rally list.
    async ensureAllRalliesLoaded() {
      if (this.drainingRallies) { return; }
      this.drainingRallies = true;
      try {
        let guard = 0; // safety cap: 50 pages x 6 = 300 rallies
        while (!this.allRalliesLoaded && this.rallyFilter.trim() && guard < 50) {
          await this.loadMoreRallies();
          guard += 1;
        }
      } finally {
        this.drainingRallies = false;
      }
    },
    openMemberSearch() {
      this.memberSearchOpen = true;
      this.$nextTick(() => this.$refs.memberSearchInput?.focus());
    },
    closeMemberList() {
      this.memberSearchOpen = false;
    },
    onMemberQueryChange() {
      clearTimeout(this.searchDebounce);
      const query = this.memberQuery;
      if (!query || query.length < 2) { return; }
      this.searchDebounce = setTimeout(() => {
        this.searchMembers({ query, rallyId: this.selectedRally.id });
      }, 300);
    },
    addMember(member) {
      if (!this.canAddRecipient) { return; }
      this.selectedMembers.push({
        userId: member.attributes["user-id"] + "",
        name: `${member.attributes["first-name"]} ${member.attributes["last-name"]}`
      });
      this.memberQuery = "";
      this.memberSearchOpen = false;
    },
    removeMember(member) {
      this.selectedMembers = this.selectedMembers.filter(m => m.userId !== member.userId);
    },
    async send() {
      if (!this.canSend || !this.messageText) { return; }
      this.sending = true;
      try {
        // 1 recipient -> 1:1 PrivateMessage channel; 2+ -> GroupMessage.
        // Both find-or-create server-side, so re-selecting the same people
        // resumes the existing thread.
        const thread = await this.fetchOrCreateThread({
          rallyId: this.selectedRally.id,
          recipientUserIds: this.selectedMembers.map(m => m.userId)
        });
        if (!thread) {
          console.log("fetchOrCreateThread returned no channel");
          return;
        }
        await this.createContents({
          contentType: "Message",
          contextId: thread.id,
          contextType: "Channel",
          type: "PrivateMessage", // content type stays PrivateMessage for both thread types (notification pipeline keys on it)
          contextChannelId: thread.id,
          contextRallyId: thread.rallyId,
          status: 1,
          body: this.messageText,
          privateMessageChannel: thread.channel
        });
        this.messageText = "";
        this.$emit("created", thread.id);
      } catch (e) {
        console.log(e);
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style scoped>
.sh-new-message-pane {
  display: flex;
  flex-direction: column;
}
.sh-new-message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e8e8e8;
}
.sh-new-message-title {
  font-size: 1.1rem;
  font-weight: 600;
}
.sh-new-message-cancel {
  background: #fff;
  color: #36a8ae;
  border: 1px solid #36a8ae;
  border-radius: 1rem;
  padding: 0.25rem 1rem;
  cursor: pointer;
  /* Override the global button rules (_button.scss): min-width 7rem,
     uppercase, margin-left. Mockup shows a compact "Cancel" pill. */
  min-width: 0;
  margin: 0;
  text-transform: none;
  font-size: 0.9rem;
  line-height: 1.4;
}
.sh-new-message-form {
  flex: 1;
  padding: 1rem 1.25rem;
}
.sh-new-message-field {
  position: relative;
  margin-bottom: 0.75rem;
}
.sh-new-message-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  cursor: pointer;
  background: #fff;
}
.sh-new-message-select-value.is-placeholder {
  color: #aaa;
}
.sh-new-message-options {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 5;
  background: #fff;
  border: 1px solid #eee;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  max-height: 240px;
  overflow-y: auto;
}
.sh-new-message-option {
  padding: 0.3rem 0.8rem;
  text-align: left;
  cursor: pointer;
}
.sh-new-message-option:nth-child(even) {
  background: #f5f5f5;
}
.sh-rally-filter-input {
  position: sticky;
  top: 0;
  z-index: 1;
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid #eee;
  outline: none;
  padding: 0.5rem 0.8rem;
  background: #fff;
  font-size: 0.9rem;
}
.sh-rally-list-note {
  color: #888;
  cursor: default;
}
.sh-rally-list-note:hover {
  background: inherit;
}
.sh-rally-list-more {
  color: #008295;
  font-weight: 600;
  text-align: center;
}
.sh-new-message-option:hover {
  background: #eef7f8;
}
.sh-new-message-recipients {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.5rem 0.8rem;
  background: #fff;
}
.sh-recipient-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #1c6b70;
  color: #fff;
  border-radius: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
}
.sh-recipient-chip i {
  cursor: pointer;
}
.sh-add-member {
  color: #36a8ae;
  cursor: pointer;
  font-size: 0.9rem;
}
.sh-member-search-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 140px;
}
.sh-member-results {
  top: 100%;
}
</style>
